import { useNavigate, useParams } from 'react-router-dom';
import MoveHouseForm from '../../../../components/forms/move-house/move-house.form';
import { ReferenceDto } from '../../../../services/dtos/reference.dto';
import React, { useEffect, useRef, useState } from 'react';
import { getPageContent } from '../../../../services/page.service';
import { showAxiosMessageError } from '../../../../services/error-message.service';
import { useSnackbars } from '../../../../hooks/snackbar.hook';
import { InterlocutorsReferenceDto } from '../../../../services/dtos/interlocutors-reference.dto';
import { getCivilities, getInterlocutorsTypes } from '../../../../services/references.service';
import { moveHouse } from '../../../../services/contracts.service';
import { MoveHouseDto } from '../../../../services/dtos/move-house.dto';
import { getAddressGouv } from '../../../../services/address.service';
import { AddressGouvFeaturesPropertiesFormDto } from '../../../../services/dtos/form/address-gouv-features-properties-form.dto';
import { AxiosError } from 'axios';

/**
 * Page déménagement
 * @constructor
 */
export default function MoveHousePage() {
    //navigate
    const navigate = useNavigate();
    //Params
    const { id } = useParams();
    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //UseState
    const [countries, setCountries] = useState<ReferenceDto[]>([{ value: 'France', label: 'France' }]);
    const [validForm, setValidForm] = useState(true);
    const [country, setSelectedCountry] = useState<ReferenceDto>({ value: 'France', label: 'France' });
    const [incomingPersonCountry, setIncomingPersonCountry] = useState<ReferenceDto>({
        value: 'France',
        label: 'France',
    });
    const [incomingPerson, setIncomingPerson] = useState('Non');
    const [isMoral, setIsMoral] = React.useState(false);
    const [civilities, setCivilities] = useState<ReferenceDto[]>([]);
    const [interlocutors, setInterlocutors] = useState<InterlocutorsReferenceDto[]>([]);
    const [addressesGouv, setAddressesGouv] = useState<AddressGouvFeaturesPropertiesFormDto[]>([]);
    const [addressesGouvIncoming, setAddressesGouvIncoming] = useState<AddressGouvFeaturesPropertiesFormDto[]>([]);

    const minSizeSearch = 3;

    //Use effect
    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;

        //Récupération des pays à afficher dans le select
        getPageContent('adresse')
            .then(async result => {
                if (result) {
                    let countriesArray: ReferenceDto[] = [];
                    JSON.parse(result[0].valeur, (key, value: string) => {
                        if (key !== null && key !== undefined && key !== '') {
                            countriesArray.push({ value: value, label: value });
                        }
                    });
                    setCountries(countriesArray);
                }
            })
            .catch(error => {
                console.error('Erreur lors de la récupération du contenu de la page');
                showAxiosMessageError(error, showMessage);
            });

        //Appel types d'interlocuteurs
        getInterlocutorsTypes()
            .then(result => {
                const data: InterlocutorsReferenceDto[] =
                    result &&
                    result.map(e => {
                        return {
                            id: e.id,
                            label: e.nom,
                            moralPerson: e.personneMorale,
                        };
                    });
                setInterlocutors(data);
            })
            .catch(error => {
                showAxiosMessageError(error, showMessage);
            });

        //Appel des civilités
        getCivilities()
            .then(result => {
                const data: ReferenceDto[] =
                    result &&
                    result.map(e => {
                        return {
                            value: e.id,
                            label: e.nom,
                        };
                    });
                setCivilities(data);
            })
            .catch(error => {
                showAxiosMessageError(error, showMessage);
            });
    }, [showMessage, id]);

    //handler submit form
    const handleSumbitForm = (form: any) => {
        setValidForm(false);
        //On mapp les données du formulaire
        //Puis on fait appel au web service
        moveHouse(mappingForm(form), id)
            .then(() => {
                showMessage.successAlert('Votre demande de déménagement est bien prise en compte');
                navigate('/contrat/' + id);
            })
            .catch(error => {
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setValidForm(true);
            });
    };

    //handler lors du changement de pays
    const handleChangeCountry = (e: React.SyntheticEvent, v: ReferenceDto | null) => {
        if (v) {
            setSelectedCountry(v);
        }
    };

    //handler lors du changement de pays entrant
    const handleChangeCountryIncomingPerson = (e: React.SyntheticEvent, v: ReferenceDto | null) => {
        if (v) {
            setIncomingPersonCountry(v);
        }
    };

    // Handler pour la recherche d'adresse principale via API data.gouv
    const handleSearchAddress = (event: object, value: string, reason: string) => {
        if (reason === 'input' && value?.trimEnd().length >= minSizeSearch) {
            getAddressGouv(value)
                .then(result => {
                    if (result) {
                        setAddressesGouv(result.features.map(feature => feature.properties));
                    }
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                });
        }
    };

    // Handler pour la recherche d'adresse de la personne entrante via API data.gouv
    const handleSearchAddressIncoming = (event: object, value: string, reason: string) => {
        if (reason === 'input' && value?.trimEnd().length >= minSizeSearch) {
            getAddressGouv(value)
                .then(result => {
                    if (result) {
                        setAddressesGouvIncoming(result.features.map(feature => feature.properties));
                    }
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                });
        }
    };

    // Handler pour la sélection d'une adresse principale
    const handleSelectAddress = (address: AddressGouvFeaturesPropertiesFormDto | null) => {
        if (address) {
            console.log('Adresse principale sélectionnée:', address);
        }
    };

    // Handler pour la sélection d'une adresse de la personne entrante
    const handleSelectAddressIncoming = (address: AddressGouvFeaturesPropertiesFormDto | null) => {
        if (address) {
            console.log('Adresse de la personne entrante sélectionnée:', address);
        }
    };

    //Mapping des données du formulaire
    const mappingForm = (form: any): MoveHouseDto => {
        //On remplis les champs en fonction de si on connait l'entrant
        //Si oui on met tout
        //Sinon l'objet est plus simple
        return incomingPerson === 'Oui'
            ? {
                  codePostal: parseInt(form.codePostal, 10),
                  dateSortie: form.dateMoving,
                  numeroRue: form.numeroRue,
                  pays: country.label,
                  releveCompteur: form.meterReading,
                  rue: form.rue,
                  ville: form.ville,
                  boitePostale: form.boitePostale || '',
                  complements: {
                      numeroRue: form.complement?.numeroRue || '',
                      rue: form.complement?.rue || '',
                      appartement: form.complement?.appartement || '',
                      batiment: form.complement?.batiment || '',
                      escalier: form.complement?.escalier || '',
                      etage: form.complement?.etage || '',
                      ville: form.complement?.ville || '',
                  },
                  entrantInfos: incomingPerson === 'Oui',
                  entrantCivilite: '/civilites/' + form.entrant.civilite,
                  entrantCodePostal: parseInt(form.entrant.adresse.codePostal, 10),
                  entrantComplements: {
                      numeroRue: form.entrant.adresse.complement.numeroRue,
                      rue: form.entrant.adresse.complement.rue,
                      appartement: form.entrant.adresse.complement.appartement,
                      batiment: form.entrant.adresse.complement.batiment,
                      escalier: form.entrant.adresse.complement.escalier,
                      etage: form.entrant.adresse.complement.etage,
                      ville: form.entrant.adresse.complement.ville,
                  },
                  entrantBoitePostale: form.entrant.adresse.boitePostale || '',
                  entrantDateNaissance: form.entrant.dateNaissance,
                  entrantEmail: form.entrant.mail,
                  entrantNom: form.entrant.nomUsage,
                  entrantNumeroRue: form.entrant.adresse.numeroRue,
                  entrantPays: incomingPersonCountry.label,
                  entrantPrenom: form.entrant.prenom,
                  entrantRue: form.entrant.adresse.rue,
                  entrantTelFixe: form.entrant.telFixe,
                  entrantTelPort: form.entrant.telPort,
                  entrantType: '/interlocuteur_types/' + form.entrant.type,
                  entrantVille: form.entrant.adresse.ville,
              }
            : {
                  codePostal: parseInt(form.codePostal, 10),
                  dateSortie: form.dateMoving,
                  numeroRue: form.numeroRue,
                  pays: country.label,
                  releveCompteur: form.meterReading,
                  rue: form.rue,
                  ville: form.ville,
                  boitePostale: form.boitePostale || '',
                  complements: {
                      numeroRue: form.complement?.numeroRue || '',
                      rue: form.complement?.rue || '',
                      appartement: form.complement?.appartement || '',
                      batiment: form.complement?.batiment || '',
                      escalier: form.complement?.escalier || '',
                      etage: form.complement?.etage || '',
                      ville: form.complement?.ville || '',
                  },
                  entrantInfos: incomingPerson === 'Oui',
              };
    };

    /**
     * Méthode qui permet de gérer le radio bouton
     * @param event
     */
    const handleChangeIncomingPerson = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIncomingPerson((event.target as HTMLInputElement).value);
    };

    /**
     * Changement de la variable isMoral au choix de l'interlocuteur (a une influence sur les champs affichés dans le formulaire
     * @param e déclencheur de l'évènement
     */
    const handleChangeInterlocutor = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.preventDefault();
        const selectedInterlocutor = interlocutors.find(interlocutor => interlocutor.id === e?.target?.value);
        setIsMoral(selectedInterlocutor?.moralPerson === undefined ? false : selectedInterlocutor.moralPerson);
    };

    /**
     * Mapping des interlocuteurs pour la liste du formulaire
     */
    const interlocutorsSelection = interlocutors.map(interlocutor => {
        return {
            value: interlocutor.id,
            label: interlocutor.label,
        };
    });

    //Retourne la formulaire
    return (
        <MoveHouseForm
            handleSumbitForm={handleSumbitForm}
            handleChangeCountry={handleChangeCountry}
            handleChangeCountryIncomingPerson={handleChangeCountryIncomingPerson}
            countriesSelection={countries}
            handleChangeIncomingPerson={handleChangeIncomingPerson}
            incomingPerson={incomingPerson}
            isMoral={isMoral}
            handleChangeInterlocutor={handleChangeInterlocutor}
            interlocutorsSelection={interlocutorsSelection}
            civilities={civilities}
            validate={validForm}
            contractId={id}
            selectedCountry={country}
            selectedIncomingPersonCountry={incomingPersonCountry}
            handleSearchAddress={handleSearchAddress}
            handleSearchAddressIncoming={handleSearchAddressIncoming}
            addressesGouv={addressesGouv}
            addressesGouvIncoming={addressesGouvIncoming}
            handleSelectAddress={handleSelectAddress}
            handleSelectAddressIncoming={handleSelectAddressIncoming}
        />
    );
}
